"use client";
import { toast } from "@rallly/ui/sonner";
import { absoluteUrl } from "@rallly/utils/absolute-url";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import React from "react";
import superjson from "superjson";
import { useTranslation } from "@/i18n/client";
import { signOut } from "@/lib/auth-client";
import { registerBrowserQueryClient } from "@/lib/query-client";
import { trpc } from "../client";
import type { AppRouter } from "../routers";

function isTRPCClientError(error: Error): error is TRPCClientError<AppRouter> {
  return error instanceof TRPCClientError;
}

// A request cancelled on unmount or navigation surfaces as an aborted tRPC
// request with no server response. Nothing broke, so it must not raise a
// "Unable to reach the server" toast.
function isAbortError(error: TRPCClientError<AppRouter>) {
  return (
    error.cause instanceof DOMException && error.cause.name === "AbortError"
  );
}

export function TRPCProvider(props: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [queryClient] = React.useState(() => {
    function handleError(error: Error) {
      if (!isTRPCClientError(error)) {
        return;
      }

      // A cancelled request is not a failure — never warn about connectivity
      if (isAbortError(error)) {
        return;
      }

      // Missing error data means no usable server response: the request
      // failed in transit or the response was not a tRPC error envelope
      // (e.g. an HTML error page from a proxy)
      if (!error.data) {
        toast.error(
          t("actionErrorNetwork", {
            defaultValue:
              "Unable to reach the server. Please check your connection and try again.",
          }),
          { id: "network-error" },
        );
        return;
      }

      switch (error.data?.code) {
        case "UNAUTHORIZED":
          // Never sign out automatically — a failed sign out turns this
          // into an infinite loop with the login page.
          if (error.data.appError === "INVALID_SESSION") {
            toast.error(
              t("actionErrorInvalidSession", {
                defaultValue: "Your session is no longer valid",
              }),
              {
                id: "invalid-session",
                duration: Number.POSITIVE_INFINITY,
                action: {
                  label: t("signOut", { defaultValue: "Sign out" }),
                  onClick: () => {
                    signOut().finally(() => {
                      window.location.href = "/login";
                    });
                  },
                },
              },
            );
          } else {
            toast.error(
              t("actionErrorUnauthorized", {
                defaultValue: "You are not authorized to perform this action",
              }),
              { id: "unauthorized" },
            );
          }
          break;
        case "FORBIDDEN":
          toast.error(
            t("actionErrorForbidden", {
              defaultValue: "You are not allowed to perform this action",
            }),
          );
          break;
        case "NOT_FOUND":
          toast.error(
            t("actionErrorNotFound", {
              defaultValue: "The resource was not found",
            }),
          );
          break;
        case "TOO_MANY_REQUESTS":
          toast.error(
            t("actionErrorTooManyRequests", {
              defaultValue: "You are making too many requests",
            }),
          );
          break;
        case "PAYLOAD_TOO_LARGE":
          toast.error(
            t("actionErrorPayloadTooLarge", {
              defaultValue:
                "The file you uploaded is too large. Please try a smaller file.",
            }),
          );
          break;
        case "PAYMENT_REQUIRED":
          toast.error(
            t("actionErrorPaymentRequired", {
              defaultValue: "You need to upgrade to perform this action",
            }),
          );
          break;
        case "SERVICE_UNAVAILABLE":
          toast.error(
            t("actionErrorServiceUnavailable", {
              defaultValue:
                "The service required to perform this action is not available",
            }),
          );
          break;
        default:
          toast.error(
            t("actionErrorInternalServerError", {
              defaultValue: "An internal server error occurred",
            }),
          );
          break;
      }
    }

    const client = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          staleTime: 1000 * 60,
        },
      },
      queryCache: new QueryCache({ onError: handleError }),
      mutationCache: new MutationCache({ onError: handleError }),
    });
    // Sign-out refreshes the router but keeps this provider mounted, so
    // signOut() clears the cache through this registration.
    registerBrowserQueryClient(client);
    return client;
  });
  const [trpcClient] = React.useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: absoluteUrl("/api/trpc"),
          transformer: superjson,
        }),
      ],
    }),
  );
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {props.children}
      </QueryClientProvider>
    </trpc.Provider>
  );
}
